<template>
    <div>
        <v-row>
            <v-col cols="6">
                <div class="pa-4 rounded fill-height zoezi-card-default square-box">
                    <h3 class="text-center font-weight-light">{{$translate('Number of visits')}}</h3>
                    <h3 class="text-center">{{ allEntries }}</h3>
                    <p class="text-center text-caption  mb-0 mt-2">{{$translate('This month')}}: {{ entriesThisMonth }}</p>
                </div>
            </v-col>
            <v-col cols="6">
                <div class="pa-4 rounded fill-height zoezi-card-default square-box">
                    <h3 class="text-center font-weight-light">{{$translate('Average per week')}}</h3>
                    <h3 class="text-center">{{ weeklyAverage.toFixed(2) }}</h3>
                    <p class="text-center text-caption mb-0 mt-2">{{$translate('Latest')}}: {{ lastEntry || $translate('n/a') }}</p>
                </div>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <chart-js-line :chart-data="data" :chart-options="options" class="pa-4 rounded zoezi-card-default"/>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line as ChartJsLine } from 'vue-chartjs/legacy'

ChartJS.register(Title, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement)

export default {
    name: "zoezi-my-entry-stats",

    zoezi: {
        title: "My entrance statistics",
        icon: 'mdi-chart-line-variant'
    },

    components: {
        ChartJsLine
    },

    props: {
        selectedDoors: Array,
        minTimeToCount: Number,
    },

    data: () => ({
        gdata: null,
        allEntries: 0,
        entriesThisMonth: 0,
        weeklyAverage: 0,
        lastEntry: null,
        primaryColor: '#00000000',
        entryDoors: [],
    }),
    computed: {
        chartFontFamily() {
            const target = document.querySelector('.v-application') || document.body;
            return window.getComputedStyle(target).fontFamily;
        },
        data() {
            const colors = ['#FF7857', '#FDD761', '#A3C86D', '#0b62a4', '#7a92a3', '#4da74d', '#afd8f8', '#edc240', '#cb4b4b', '#9440ed'];
            let res = {
                labels: [
                    'Jan',
                    'Feb',
                    'Mar',
                    'Apr',
                    'May',
                    'Jun',
                    'Jul',
                    'Aug',
                    'Sep',
                    'Oct',
                    'Nov',
                    'Dec',
                ]
            };
            res.labels.forEach((x, i) => {
                res.labels[i] = this.$translate(x);
            });
            let datasets = [];
            for (let year in this.gdata) {
                let color = colors[datasets.length % colors.length];
                datasets.push({
                    label: year,
                    backgroundColor: color,
                    tension: .25,
                    fill: true,
                    borderColor: color,
                    pointStyle: false,
                    data: this.gdata[year].entries
                });
            }
            // Reverse order
            datasets.reverse();
            res.datasets = datasets;

            return res;
        },
        options() {
            return {
                responsive: true,
                maintainAspectRatio: false,
                scales: {
                    y: {
                        beginAtZero: true,
                        ticks: {
                            stepSize: 1
                        },
                        title: {
                            display: true,
                            text: this.$translate('Number of visits')
                        }
                    },
                },
                plugins: {
                    title: {
                        display: true,
                        text: this.$translate('Entry'),
                        font: {
                            size: 24,
                            family: this.chartFontFamily
                        }
                    },
                    legend: {
                        display: true,
                        position: 'top',
                        align: 'right',
                        labels: {
                            usePointStyle: true,
                            pointStyle: 'circle',
                            padding: 12,
                            font: {
                                size: 14,
                                weight: 700
                            }
                        },
                    }
                }
            }
        }
    },
    mounted() {
        this.primaryColor = this.$root.colorToHex('primary');

        let fromDate = new Date().addMonths(-12);
        let toDate = Date.today();
        this.$api.get('/api/memberapi/stats', { fromDate: fromDate.yyyymmdd(), toDate: toDate.yyyymmdd(), types: 'entries_extra,bookings' }).then(data => {
            data.entries = data.entries_extra; // Extra entires includes door & time
            // Remove all doors that should not be present in stats. Include all entries from 'default'/'null' door.
            if (this.selectedDoors?.length) {
                for (var key in data.entries) {
                    if (!this.selectedDoors.includes(data.entries[key][1]) && data.entries[key][1] != null) {
                        delete data.entries[key];
                    }
                }
            }

            // Chart
            let d = Date.today().yearsAgo(3).yyyymmddSpacehhmmss();
            const firstEntryKey = Object.keys(data?.entries ?? {})[0];
            if (firstEntryKey && firstEntryKey > d) {
                d = Date.newFull(firstEntryKey);
            }
            let gdata = {};

            let lastDate = null;
            for (const key in data.entries) {
                this.lastEntry = key;

                const date = Date.newFull(key);
                const year = date.getFullYear();
                const month = date.getMonth();
                const day = date.getDate();

                // Initialize the year in gdata if it's not already present
                if (!gdata[year]) {
                    gdata[year] = {
                        year: year,
                        entries: new Array(12).fill(0),
                        bookings: new Array(12).fill(0)
                    };
                }

                const diffHours = (date - (lastDate ? lastDate : 0))/60/60/1000 // millis -> hours
                if (diffHours > this.minTimeToCount || day > lastDate.getDate()) {
                    gdata[year].entries[month]++;
                    lastDate = date;
                }
            }

            for (const key in data.bookings) {
                const date = Date.newFull(key);
                const year = date.getFullYear();
                const month = date.getMonth();

                // Initialize the year in gdata if it's not already present
                if (!gdata[year]) {
                    gdata[year] = {
                        year: year,
                        entries: new Array(12).fill(0),
                        bookings: new Array(12).fill(0)
                    };
                }

                // Add the booking count for the corresponding month (subtracting 1 because months are 0-indexed in the bookings array)
                gdata[year].bookings[month - 1] += data.bookings[key];
            }

            this.gdata = gdata;

            let totalEntryCount = 0;
            let lastMonthsCount = 0;
            const startCountDate = new Date()
            startCountDate.setMonth(startCountDate.getMonth() - 1);

            // Iterate over each year in the data
            for (const year in gdata) {
                const yearData = gdata[year];
                const entries = yearData.entries;

                // Iterate over each month's entries
                for (let month = 0; month < entries.length; month++) {
                    // Update the total entry count
                    totalEntryCount += entries[month];

                    const currentDate = new Date();
                    if (currentDate > startCountDate) {
                        lastMonthsCount += entries[month];
                    }
                }
            }

            // Calculate weekly average
            const firstDate = data?.entries && Object.keys(data.entries)[0];
            if (firstDate) {
                const millisPerWeek = 1000 * 60 * 60 * 24 * 7;
                let actualWeeks = (new Date() - Date.newFull(firstDate)) / millisPerWeek;
                if (actualWeeks > 52) { actualWeeks = 52 }
                const weeklyAverage = lastMonthsCount / (actualWeeks < 1 ? 1 : actualWeeks);

                this.allEntries = totalEntryCount;
                this.entriesThisMonth = gdata[new Date().getFullYear()].entries[new Date().getMonth()];
                this.weeklyAverage = weeklyAverage;
            }
        });

    }
}
</script>
